import type { User } from 'oidc-react';
import * as Sentry from '@sentry/react';
import { userManager } from './userManager';

const LEGACY_RETURN_TO_KEY = 'afterLoginRedirect';
const DEFAULT_RETURN_TO = '/';

export type AuthTransactionState = {
	returnTo: string;
};

const getCallbackPath = () => {
	try {
		return new URL(
			import.meta.env.VITE_OIDC_REDIRECT_URI,
			window.location.origin,
		).pathname;
	} catch {
		return '/success';
	}
};

export const normalizeReturnTo = (value: unknown): string | null => {
	if (
		typeof value !== 'string' ||
		!value.startsWith('/') ||
		value.startsWith('//')
	) {
		return null;
	}

	try {
		const url = new URL(value, window.location.origin);
		if (
			url.origin !== window.location.origin ||
			url.pathname === getCallbackPath()
		) {
			return null;
		}

		return `${url.pathname}${url.search}${url.hash}`;
	} catch {
		return null;
	}
};

export const getCurrentReturnTo = () =>
	normalizeReturnTo(
		`${window.location.pathname}${window.location.search}${window.location.hash}`,
	) || DEFAULT_RETURN_TO;

export const getReturnToFromState = (state: unknown) => {
	if (!state || typeof state !== 'object' || !('returnTo' in state)) {
		return null;
	}

	return normalizeReturnTo((state as AuthTransactionState).returnTo);
};

export const consumeLegacyReturnTo = () => {
	let storedValue: string | null;

	try {
		storedValue = window.localStorage.getItem(LEGACY_RETURN_TO_KEY);
	} catch {
		return null;
	}

	try {
		window.localStorage.removeItem(LEGACY_RETURN_TO_KEY);
	} catch {
		// The transaction state remains authoritative when storage is unavailable.
	}

	if (!storedValue) {
		return null;
	}

	try {
		return normalizeReturnTo(JSON.parse(storedValue) as unknown);
	} catch {
		return null;
	}
};

let silentRenewPromise: Promise<User | null> | null = null;

export const renewUser = () => {
	if (!silentRenewPromise) {
		silentRenewPromise = userManager.signinSilent().finally(() => {
			silentRenewPromise = null;
		});
	}

	return silentRenewPromise;
};

let signInRedirectPromise: Promise<void> | null = null;

export const redirectToSignIn = (clearUser = false) => {
	if (!signInRedirectPromise) {
		signInRedirectPromise = (async () => {
			if (clearUser) {
				await userManager.removeUser();
			}

			const state: AuthTransactionState = {
				returnTo: getCurrentReturnTo(),
			};

			await userManager.signinRedirect({ state });
		})().catch(error => {
			signInRedirectPromise = null;
			throw error;
		});
	}

	return signInRedirectPromise;
};

// The identity server validates returnUrl against the app's allowed prefixes.
// Landing on the callback without a code starts a fresh sign-in.
const SIGN_OUT_RETURN_URL = import.meta.env.VITE_OIDC_REDIRECT_URI;

// Must run while the user is still stored: oidc-client-ts revokes the refresh
// token and sends id_token_hint from it, then removes the user.
export const redirectToSignOut = () =>
	userManager.signoutRedirect({
		extraQueryParams: {
			returnUrl: SIGN_OUT_RETURN_URL,
		},
	});

// The identity server's logout without the OIDC round trip (no revoke, no
// id_token_hint), for when signoutRedirect itself failed. It still ends the
// identity session, so the next load doesn't sign straight back in.
export const IDENTITY_SIGN_OUT_URL = `${
	import.meta.env.VITE_OIDC_AUTHORITY
}/connect/logout?returnUrl=${encodeURIComponent(SIGN_OUT_RETURN_URL)}`;

const REAUTHENTICATION_ERROR_CODES = new Set([
	'account_selection_required',
	'consent_required',
	'interaction_required',
	'invalid_grant',
	'login_required',
]);

export const requiresReauthentication = (error: unknown) => {
	if (!error || typeof error !== 'object' || !('error' in error)) {
		return false;
	}

	const errorCode = (error as { error?: unknown }).error;
	return (
		typeof errorCode === 'string' &&
		REAUTHENTICATION_ERROR_CODES.has(errorCode)
	);
};

export const handleRenewError = async (error: unknown) => {
	if (requiresReauthentication(error)) {
		await redirectToSignIn(true);
	} else {
		Sentry.captureException(error, {
			level: 'warning',
			tags: { section: 'oidc-silent-renew' },
		});
	}
};

userManager.events.addAccessTokenExpiring(() => {
	void renewUser()
		.then(user => {
			if (!user?.access_token) {
				return redirectToSignIn(true);
			}
		})
		.catch(handleRenewError)
		.catch(error => Sentry.captureException(error));
});
